import { hooks } from '@approvalmax/utils';
import { useSortedCompanies } from 'modules/common';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { routingService } from 'services/routing';
import { useIntegrateReceiptBankCompany } from 'shared/data/webApp/v1';

import { fieldNameCompanyName, fieldNameCompanySelectedId } from '../constants';
import { createAllowAccessUrl } from '../helpers/createAllowAccessUrl';
import { ReceiptBankAuthEmailFormData } from '../types';

export const useReceiptBankCompanies = () => {
    const [callbackUrlParam] = hooks.useQueryParam('callback_url');
    const callbackUrl = decodeURIComponent(callbackUrlParam || '');

    const companiesAvailable = useSortedCompanies();
    const companies = useMemo(
        () => companiesAvailable.filter((company) => company.flags.isManager || company.flags.isWorkflowManager),
        [companiesAvailable]
    );

    const { mutate, isLoading } = useIntegrateReceiptBankCompany({
        onSuccess: ({ accessToken }) => {
            if (callbackUrl) {
                routingService.navigateToExternalUrl(createAllowAccessUrl(accessToken, callbackUrl));
            }
        },
    });

    const form = useForm<ReceiptBankAuthEmailFormData>({
        defaultValues: {
            [fieldNameCompanyName]: companies[0]?.displayName || null,
            [fieldNameCompanySelectedId]: companies[0]?.id || null,
        },
    });

    const onSubmit = form.handleSubmit((data) => {
        const companySelected =
            companiesAvailable.find((company) => company.id === data[fieldNameCompanySelectedId]) || null;

        if (companySelected && callbackUrl) {
            mutate({ body: { companyId: companySelected.id } });
        }
    });

    const onCancel = useCallback(() => {
        if (callbackUrl) {
            routingService.navigateToExternalUrl(callbackUrl);
        }
    }, [callbackUrl]);

    return {
        form,
        companies,
        isLoading,
        onSubmit,
        onCancel,
    };
};
